import { ANONYMOUS_ID, UTM } from "./config";
import { APP_ENV, APP_VERSION, isBrowser } from "./env";
import { sanitizeUrl } from "./masking";
import { getCurrentSession } from "./session";
import type { EnrichedEvent } from "./types";
import { getFirstTouch, getLastTouch, getUtm } from "./utm";

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return "a-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function getAnonymousId(): string {
  if (!isBrowser()) return "ssr-anon";
  try {
    const created = Number(localStorage.getItem(ANONYMOUS_ID.created_at_key) || 0);
    const now = Date.now();
    const stale = created > 0 && now - created > ANONYMOUS_ID.rotation_ms;
    let id = localStorage.getItem(ANONYMOUS_ID.storage_key);
    if (!id || stale) {
      id = uuid();
      localStorage.setItem(ANONYMOUS_ID.storage_key, id);
      localStorage.setItem(ANONYMOUS_ID.created_at_key, String(now));
    }
    return id;
  } catch {
    return "anon-fallback-" + Math.random().toString(36).slice(2);
  }
}

export function resetAnonymousId(): void {
  if (!isBrowser()) return;
  try {
    localStorage.removeItem(ANONYMOUS_ID.storage_key);
    localStorage.removeItem(ANONYMOUS_ID.created_at_key);
  } catch {
    /* noop */
  }
}

let cachedUserId: string | null = null;
export function setUserId(id: string | null): void {
  cachedUserId = id;
}

function detectDeviceType(): EnrichedEvent["device_type"] {
  if (!isBrowser()) return "desktop";
  const w = window.innerWidth;
  if (w < 640) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

function detectLocale(): EnrichedEvent["page_locale"] {
  if (!isBrowser()) return "unknown";
  const p = window.location.pathname;
  if (p.startsWith("/en/") || p === "/en") return "en";
  return "pt";
}

export function buildEnvelope(name: string, payload: Record<string, unknown>, flags: {
  consentReplayable: boolean;
  essentialSinksOnly: boolean;
  priority: "high" | "low";
}): EnrichedEvent {
  const session = getCurrentSession();
  const utm = getUtm();
  return {
    name,
    payload,
    consentReplayable: flags.consentReplayable,
    essentialSinksOnly: flags.essentialSinksOnly,
    priority: flags.priority,
    anonymous_id: getAnonymousId(),
    user_id: cachedUserId,
    session_id: session.id,
    timestamp: new Date().toISOString(),
    app_env: APP_ENV,
    app_version: APP_VERSION,
    page_path: isBrowser() ? window.location.pathname + window.location.search : "",
    page_locale: detectLocale(),
    page_referrer: sanitizeUrl(isBrowser() ? document.referrer || null : null, UTM.strip_params),
    device_type: detectDeviceType(),
    viewport: isBrowser() ? { w: window.innerWidth, h: window.innerHeight } : { w: 0, h: 0 },
    is_authenticated: cachedUserId !== null,
    utm,
    first_touch: getFirstTouch(),
    last_touch: getLastTouch(),
  };
}
